// Draws a badge onto a canvas at print resolution. Used for the shared/printed PDF so the result does
// not depend on html2canvas (which ignores object-fit and stretches the QR code).

export const PRINT_DPI = 300;
const PLACEHOLDER = /via\.placeholder\.com/;

/** Largest box with the image's aspect ratio that fits inside (contain) or covers (cover) the target. */
export function fitRect(imageWidth, imageHeight, boxWidth, boxHeight, mode = 'contain') {
  const scale = mode === 'cover'
    ? Math.max(boxWidth / imageWidth, boxHeight / imageHeight)
    : Math.min(boxWidth / imageWidth, boxHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return { x: (boxWidth - width) / 2, y: (boxHeight - height) / 2, width, height };
}

/** Greedy word wrap; words longer than the line are broken by character. */
export function wrapLines(text, maxWidth, measure) {
  const lines = [];
  let line = '';
  const push = (word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate) <= maxWidth || !line) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  };
  String(text).split(/\s+/).filter(Boolean).forEach((word) => {
    if (measure(word) <= maxWidth) {
      push(word);
      return;
    }
    let chunk = '';
    [...word].forEach((char) => {
      if (measure(chunk + char) > maxWidth && chunk) {
        push(chunk);
        chunk = char;
      } else {
        chunk += char;
      }
    });
    if (chunk) push(chunk);
  });
  if (line) lines.push(line);
  return lines;
}

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

/**
 * fields: [{ type, x, y, width, height (inches), value, style }] with values already resolved.
 * Printed badges are black on white; emailed certificates pass color + background.
 */
export async function drawBadge({ widthIn, heightIn, fields, fontSizeFor, dpi = PRINT_DPI, background = null, color = false }) {
  const px = (inches) => inches * dpi;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(px(widthIn));
  canvas.height = Math.round(px(heightIn));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (background) {
    const backgroundImage = await loadImage(background);
    if (backgroundImage) ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
  }
  if (document.fonts?.ready) await document.fonts.ready;

  const images = await Promise.all(fields.map((field) =>
    (field.type !== 'text' && field.value && !PLACEHOLDER.test(field.value) ? loadImage(field.value) : null)));

  fields.forEach((field, index) => {
    const w = px(field.width);
    const h = px(field.height);
    ctx.save();
    ctx.translate(px(field.x) + w / 2, px(field.y) + h / 2);
    const rotation = field.style?.rotation || 0;
    if (rotation) ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-w / 2, -h / 2);
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();

    if (field.type === 'text') {
      const fontPx = (fontSizeFor(field) / 72) * dpi;
      ctx.font = `${field.style?.fontWeight || 'normal'} ${fontPx}px ${field.style?.fontFamily || 'Arial, sans-serif'}`;
      ctx.fillStyle = color ? (field.style?.color || '#000000') : '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const lineHeight = fontPx * 1.2;
      const lines = field.style?.overflow === 'shrink'
        ? [String(field.value)]
        : wrapLines(field.value, w, (t) => ctx.measureText(t).width);
      const top = h / 2 - ((lines.length - 1) * lineHeight) / 2;
      lines.forEach((text, i) => ctx.fillText(text, w / 2, top + i * lineHeight));
    } else if (images[index]) {
      const img = images[index];
      const isQr = field.type === 'qrCode';
      const rect = fitRect(img.naturalWidth, img.naturalHeight, w, h, isQr ? 'contain' : 'cover');
      // Hard pixel edges keep QR modules scannable; photos go grayscale.
      ctx.imageSmoothingEnabled = !isQr;
      if (!isQr && !color) ctx.filter = 'grayscale(1)';
      ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
    }
    ctx.restore();
  });
  return canvas;
}
